// v4 duck — REDESIGNED. A real mallard riding the water: a long hull body
// with a chestnut breast, folded wing slabs carrying the blue speculum patch
// with a white bar, a white undertail and a perky up-curled drake tail, a
// white neck ring under a bottle-green head, and a flat yellow bill with a
// dark nail. Two plumages: v0 the drake, v1 the mottled brown hen (orange
// bill, no ring, no curl — but she keeps the blue speculum).
//
// Ported 1:1 from age-of-mcp's v4/duck.ts, using the library's shared
// `wetland.eyes` helper — identical to the v4 source's.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const { eyes } = wetland;

const PLUMAGES = [
  { // drake
    body: "#8a7a64", breast: "#5c3a28", flank: "#b8aa92", wing: "#74644e",
    tail: "#3e342a", under: "#e8e4da", head: "#1e7a48", bill: "#e0b020",
    drake: true,
  },
  { // hen
    body: "#8a6a42", breast: "#9a7a50", flank: "#a8895e", wing: "#6e5230",
    tail: "#6e5436", under: "#d8c8aa", head: "#7a5c38", bill: "#d89030",
    drake: false,
  },
];

function partsFor(v: number): Part[] {
  const c = PLUMAGES[v % PLUMAGES.length]!;
  const parts: Part[] = [
    // hull body on the waterline (dips just below, like the classic)
    { geometry: new THREE.IcosahedronGeometry(0.038, 0), color: c.body,
      position: [0, 0.02, 0], scale: [1.65, 0.85, 1.0] },
    // chestnut breast
    { geometry: new THREE.IcosahedronGeometry(0.025, 0), color: c.breast,
      position: [0.04, 0.026, 0], scale: [1.0, 1.0, 0.95] },
    // pale flank panel
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: c.flank,
      position: [-0.005, 0.024, 0], scale: [1.35, 0.75, 0.95] },
    // folded wing slabs
    { geometry: new THREE.IcosahedronGeometry(0.026, 0), color: c.wing,
      position: [-0.012, 0.034, 0.024], scale: [1.5, 0.55, 0.5] },
    { geometry: new THREE.IcosahedronGeometry(0.026, 0), color: c.wing,
      position: [-0.012, 0.034, -0.024], scale: [1.5, 0.55, 0.5] },
    // blue speculum patch with a white trailing bar, one per wing
    { geometry: new THREE.BoxGeometry(0.022, 0.004, 0.009), color: "#3a6ed8",
      position: [-0.022, 0.045, 0.029], rotation: [0, 0, 0.12] },
    { geometry: new THREE.BoxGeometry(0.022, 0.004, 0.009), color: "#3a6ed8",
      position: [-0.022, 0.045, -0.029], rotation: [0, 0, 0.12] },
    { geometry: new THREE.BoxGeometry(0.02, 0.003, 0.004), color: "#eef0f2",
      position: [-0.024, 0.042, 0.0335], rotation: [0, 0, 0.12] },
    { geometry: new THREE.BoxGeometry(0.02, 0.003, 0.004), color: "#eef0f2",
      position: [-0.024, 0.042, -0.0335], rotation: [0, 0, 0.12] },
    // white undertail + perky tail (tip swept back-up)
    { geometry: new THREE.IcosahedronGeometry(0.014, 0), color: c.under,
      position: [-0.05, 0.022, 0], scale: [1.0, 0.7, 1.0] },
    { geometry: new THREE.ConeGeometry(0.012, 0.024, 4), color: c.tail,
      position: [-0.062, 0.036, 0], rotation: [0, 0, Math.PI / 2 - 0.28],
      scale: [1, 1, 0.6] },
    // neck and head
    { geometry: new THREE.CylinderGeometry(0.012, 0.014, 0.025, 5), color: c.head,
      position: [0.052, 0.042, 0], rotation: [0, 0, -0.35] },
    { geometry: new THREE.IcosahedronGeometry(0.019, 0), color: c.head,
      position: [0.065, 0.062, 0], scale: [1.25, 1.0, 0.95] },
    // flat bill with a dark nail at the tip
    { geometry: new THREE.ConeGeometry(0.009, 0.028, 4), color: c.bill,
      position: [0.09, 0.057, 0], rotation: [0, 0, -Math.PI / 2],
      scale: [0.6, 1, 1.4] },
    { geometry: new THREE.IcosahedronGeometry(0.003, 0), color: "#3a342a",
      position: [0.104, 0.0575, 0] },
    ...eyes(0.068, 0.068, 0.0135, 0.0035),
  ];
  if (c.drake) {
    // white neck ring and the drake's curled tail feather
    parts.push(
      { geometry: new THREE.CylinderGeometry(0.0125, 0.0135, 0.006, 6),
        color: "#f2f4f6", position: [0.0555, 0.0465, 0], rotation: [0, 0, -0.35] },
      { geometry: new THREE.IcosahedronGeometry(0.006, 0), color: c.tail,
        position: [-0.073, 0.042, 0] },
    );
  }
  return parts;
}

export const duckV4 = defineModel({
  id: "duck-v4",
  batchId: "polished",
  name: "Duck (v4)",
  tags: ["creature","duck","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly duck.",
  description: "Ported from the age-of-mcp gallery, v4 style.",
  author: "age-of-mcp",
  footprint: 0.08,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => partsFor(0) },
    { id: "variant-1", label: "Variant 2", parts: () => partsFor(1) },
  ],
  animations: [],
});
